// app/javascript/pages/ChatPage.js
import React from "react";
import {
  useEffect,
  useRef,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { Box, FormControl, ListSubheader, MenuItem, Select, useMediaQuery } from "@mui/material";
import ChatBox from "../components/ChatBox";
import { Navbar } from "../components/Navbar";
import Input from "../components/Input";
import {
  regenerateQuestionRecommendations,
  updateUserReview,
  markChatThreadRead,
  recordServiceTipPromptShown,
  dismissServiceTipPrompt,
  createServiceTipCheckoutSession,
  getServiceTipPaymentMethodStatus,
  payServiceTipWithSavedPaymentMethod,
  getAdminChatModelCatalog,
} from "../api/message";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import TermsModal from "../components/TermsModal";
import AddAppHomeModal from "../components/AddAppHomeModal";
import DownloadAppBanner from "../components/DownloadAppBanner";
import SignUpModalV2 from "../components/SignUpModalV2";
import PaywallModal from "../components/PaywallModal";
import PastDueModal from "../components/PastDueModal";
import InAppNotificationModal from "../components/Notifications/InAppNotificationModal";
import { getDeviceType } from "../utils/user_agent";
import { connectUserThreadUpdates } from "../components/Chat/websocket";
import { syncCableIdentity } from "../components/Chat/cableClient";
import {
  firstUserMessage,
  askShareMessage,
  startPlaceholderText,
  firstUserPlaceholderText,
} from "../utils/constants";
import { SubscriptionOfferCoupon } from "../components/SubscriptionCancellationModal";
import { useAppContext } from "../contexts/AppContext";
import { useChatContext } from "../contexts/ChatContext";
import { useLiveQuoteActions } from "../contexts/LiveQuoteContext";
import Sidebar from "../components/Chat/Sidebar";
import { v4 as uuid } from "uuid";
import useChatLayout from "../hooks/useChatLayout";
import useChatBootstrapThreads from "../hooks/useChatBootstrapThreads";
import useChatDispatch from "../hooks/useChatDispatch";
import useChatPendingSignup from "../hooks/useChatPendingSignup";
import useChatThreadNavigation from "../hooks/useChatThreadNavigation";
import useChatThreadSubscriptions from "../hooks/useChatThreadSubscriptions";
import { isMobile } from "react-device-detect";
import DataOutput from "../components/Chat/DataOutput";
import { filesToAttachments } from "../utils/images_uploads_attachments";
import ProBanner from "../components/ProBanner";
import ReviewModal from "../components/ReviewModal";
import ReferralInviteModal from "../components/Referrals/ReferralInviteModal";
import ServiceTipPromptModal from "../components/ServiceTips/ServiceTipPromptModal";
import TextMessagingSetupModal from "../components/TextMessagingSetupModal";
import { reconcileFinalBotMessage } from "./chatMessageReconciliation";
import {
  getUserNotificationsPage,
  markNotificationSeen,
} from "../api/notifications";
import { isInAppModalNotification } from "../components/Notifications/inAppModalUtils";
import { trackFrontendEvent } from "../utils/tracking";
import {
  clearPendingSecondQuestion,
  pendingSecondQuestionEventMetadata,
  readPendingSecondQuestion,
  writePendingSecondQuestion,
} from "../utils/signup_modal";
import {
  rememberActiveChatThread,
  readRememberedActiveChatThread,
  readRememberedActiveChatThreadByUuid,
} from "../utils/chat_thread_persistence";
import {
  moveThreadMessages,
} from "../components/Chat/chatThreadMessagesModel";
import { moveThreadDispatch } from "../components/Chat/chatDispatchRegistry";

const hideQuestionButtonsForFlow = (flow) => ({
  ...flow,
  review: false,
  line: false,
  text: false,
  question1: [false, flow.question1[1], flow.question1[2], flow.question1[3], flow.question1[4], flow.question1[5]],
  question2: [false, flow.question2[1], flow.question2[2], flow.question2[3], flow.question2[4], flow.question2[5]],
  regen: false,
});

const hasText = (value) => String(value || "").trim().length > 0;
const ADMIN_BASE_DIRECTIVE_MODEL_STORAGE_KEY = "incite.adminBaseDirectiveModelName";
const ADMIN_BASE_DIRECTIVE_REASONING_STORAGE_KEY = "incite.adminBaseDirectiveReasoningEffort";
const LOCAL_EPOCH_ID_FLOOR = 1_000_000_000_000;

const hasPersistedMessageId = (value) => {
  if (value == null || value === "") return false;
  const asString = String(value);
  if (asString.startsWith("temp-")) return false;
  const numeric = Number(asString);
  return !Number.isFinite(numeric) || numeric < LOCAL_EPOCH_ID_FLOOR;
};

const providerLabel = (providerKind) => {
  switch (providerKind) {
    case "openai":
      return "GPT";
    case "bedrock":
      return "Anthropic / Bedrock";
    case "deepseek":
      return "DeepSeek";
    default:
      return "Other";
  }
};

const adminModelCostLabel = (model) =>
  model?.token_costs?.label || model?.tokenCosts?.label || "Pricing unavailable";

const adminModelCostAvailable = (model) => {
  const tokenCosts = model?.token_costs || model?.tokenCosts;
  return Boolean(tokenCosts?.available);
};

const AdminChatModelSwitcher = ({
  catalog,
  selectedModelName,
  onSelectedModelNameChange,
  selectedReasoningEffort,
  onSelectedReasoningEffortChange,
  loading,
  error,
  chatLayout,
}) => {
  const models = Array.isArray(catalog?.models) ? catalog.models : [];
  const contentLeft = Number(chatLayout?.contentLeft || 0);
  const contentRight = Number(chatLayout?.contentRight || 0);
  const groupedModels = models.reduce((memo, model) => {
    const group = providerLabel(model?.provider_kind);
    memo[group] = memo[group] || [];
    memo[group].push(model);
    return memo;
  }, {});
  const defaultLabel = catalog?.default_model_name
    ? `Default (${catalog.default_model_name})`
    : "Default";
  const defaultModel = models.find((model) => model.model_name === catalog?.default_model_name);
  const selectedModel =
    models.find((model) => model.model_name === selectedModelName) || defaultModel;
  const rawReasoningOptions =
    selectedModel?.reasoning_effort_options ||
    selectedModel?.reasoningEffortOptions ||
    selectedModel?.reasoning_efforts ||
    selectedModel?.reasoningEfforts ||
    [];
  const reasoningOptions = rawReasoningOptions
    .map((option) => (
      typeof option === "string"
        ? { value: option, label: option === "xhigh" ? "XHigh" : `${option.charAt(0).toUpperCase()}${option.slice(1)}` }
        : option
    ))
    .filter((option) => option?.value);
  const selectedCostLabel = loading ? "Costs loading..." : adminModelCostLabel(selectedModel);
  const renderModelMenuRow = (label, model) => (
    <Box
      sx={{
        alignItems: "center",
        display: "flex",
        gap: 2,
        justifyContent: "space-between",
        minWidth: 0,
        width: "100%",
      }}
    >
      <Box
        component="span"
        sx={{
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Box>
      <Box
        component="span"
        sx={{
          color: adminModelCostAvailable(model) ? "#9CA3AF" : "#64748B",
          flexShrink: 0,
          fontSize: 11,
          fontWeight: 700,
          whiteSpace: "nowrap",
        }}
      >
        {adminModelCostLabel(model)}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        alignItems: "flex-start",
        display: "flex",
        gap: 1,
        position: "fixed",
        top: "10px",
        left: `${contentLeft + 16}px`,
        zIndex: 1400,
        maxWidth: `calc(100vw - ${contentLeft + contentRight + 32}px)`,
        pointerEvents: "auto",
      }}
    >
      <FormControl
        size="small"
        data-admin-chat-model-switcher
        sx={{
          flex: "0 1 224px",
          minWidth: 168,
          width: 224,
          "& .MuiOutlinedInput-root": {
            borderRadius: "999px",
            fontSize: 12,
            backgroundColor: "#020617",
            color: "#E5E7EB",
            paddingRight: "8px",
            boxShadow: "0 0 0 1px rgba(59, 130, 246, 0.14)",
            "& .MuiSelect-select": {
              pt: 1.25,
              pb: 1,
              pl: 1.75,
              pr: 3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
            "& fieldset": {
              borderColor: "#374151",
            },
            "&:hover fieldset": {
              borderColor: "#9CA3AF",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#3B82F6",
            },
          },
          "& .MuiSelect-icon": {
            color: "#9CA3AF",
          },
        }}
      >
        <Select
          aria-label="Admin base directive model"
          value={selectedModelName || ""}
          disabled={loading || models.length === 0}
          onChange={(event) => onSelectedModelNameChange(event.target.value)}
          displayEmpty
          renderValue={(selected) => {
            if (!selected) return loading ? "Loading models..." : defaultLabel;

            const selectedModel = models.find((model) => model.model_name === selected);
            return selectedModel?.label || selected;
          }}
          MenuProps={{
            disableScrollLock: true,
            variant: "menu",
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            },
            sx: {
              zIndex: 2200,
            },
            MenuListProps: {
              "data-admin-chat-model-menu": "true",
              sx: {
                pointerEvents: "auto",
              },
            },
            PaperProps: {
              sx: {
                mt: 0.75,
                width: 520,
                maxWidth: "calc(100vw - 32px)",
                maxHeight: "min(540px, calc(100vh - 80px))",
                zIndex: 2201,
                pointerEvents: "auto",
                backgroundColor: "#020617",
                color: "#E5E7EB",
                boxShadow: "0 10px 30px rgba(0,0,0,0.7)",
                "& .MuiMenuItem-root": {
                  fontSize: 13,
                  transition: "background-color 140ms ease, color 140ms ease",
                },
                "& .MuiMenuItem-root:hover, & .MuiMenuItem-root.Mui-focusVisible": {
                  backgroundColor: "rgba(55,65,81,0.56)",
                },
                "& .MuiMenuItem-root.Mui-selected": {
                  backgroundColor: "rgba(55,65,81,0.8)",
                },
                "& .MuiMenuItem-root.Mui-selected:hover": {
                  backgroundColor: "rgba(55,65,81,0.92)",
                },
                "& .MuiMenuItem-root.Mui-selected.Mui-focusVisible": {
                  backgroundColor: "rgba(55,65,81,0.9)",
                },
              },
            },
          }}
        >
          <MenuItem value="">
            {renderModelMenuRow(loading ? "Loading models..." : defaultLabel, defaultModel)}
          </MenuItem>
          {Object.entries(groupedModels).flatMap(([group, groupModels]) => [
            <ListSubheader
              key={`group-${group}`}
              sx={{
                backgroundColor: "#020617",
                color: "#8B97B3",
                fontSize: 11,
                fontWeight: 800,
                lineHeight: "28px",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              {group}
            </ListSubheader>,
            ...groupModels.map((model) => (
              <MenuItem
                key={model.model_name}
                value={model.model_name}
                data-admin-chat-model-name={model.model_name}
              >
                {renderModelMenuRow(model.label || model.model_name, model)}
              </MenuItem>
            )),
          ])}
        </Select>
      </FormControl>
      {reasoningOptions.length > 0 && (
        <FormControl
          size="small"
          data-admin-chat-reasoning-switcher
          sx={{
            flex: "0 0 144px",
            width: 144,
            "& .MuiOutlinedInput-root": {
              borderRadius: "999px",
              fontSize: 12,
              backgroundColor: "#020617",
              color: "#E5E7EB",
              boxShadow: "0 0 0 1px rgba(59, 130, 246, 0.14)",
              "& .MuiSelect-select": {
                pt: 1.25,
                pb: 1,
                pl: 1.75,
                pr: 3,
              },
              "& fieldset": {
                borderColor: "#374151",
              },
              "&:hover fieldset": {
                borderColor: "#9CA3AF",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#3B82F6",
              },
            },
            "& .MuiSelect-icon": {
              color: "#9CA3AF",
            },
          }}
        >
          <Select
            aria-label="Admin base directive reasoning effort"
            value={selectedReasoningEffort || ""}
            onChange={(event) => onSelectedReasoningEffortChange(event.target.value)}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) return "Reasoning";

              return reasoningOptions.find((option) => option.value === selected)?.label || selected;
            }}
            MenuProps={{
              disableScrollLock: true,
              sx: { zIndex: 2200 },
              MenuListProps: {
                "data-admin-chat-reasoning-menu": "true",
              },
              PaperProps: {
                sx: {
                  mt: 0.75,
                  zIndex: 2201,
                  backgroundColor: "#020617",
                  color: "#E5E7EB",
                },
              },
            }}
          >
            <MenuItem value="">Chat default</MenuItem>
            {reasoningOptions.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                data-admin-chat-reasoning-effort={option.value}
              >
                {option.label || option.value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <Box
        component="span"
        data-admin-chat-model-cost
        title={selectedCostLabel}
        sx={{
          alignItems: "center",
          backgroundColor: "rgba(2, 6, 23, 0.92)",
          border: "1px solid rgba(55, 65, 81, 0.95)",
          borderRadius: "999px",
          boxShadow: "0 0 0 1px rgba(59, 130, 246, 0.08)",
          color: adminModelCostAvailable(selectedModel) ? "#CBD5E1" : "#94A3B8",
          display: { xs: "none", sm: "inline-flex" },
          flexShrink: 0,
          fontSize: 11,
          fontWeight: 800,
          height: 39,
          maxWidth: 220,
          overflow: "hidden",
          px: 1.5,
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {selectedCostLabel}
      </Box>
      {error && (
        <Box component="span" sx={{ color: "#fca5a5", fontSize: 11, mt: "4px", whiteSpace: "nowrap" }}>
          Catalog unavailable
        </Box>
      )}
    </Box>
  );
};

const isTempChatThreadUuid = (threadUuid) =>
  String(threadUuid || "").startsWith("temp-");

const isPersistedChatThreadUuid = (threadUuid) => {
  const normalizedThreadUuid = String(threadUuid || "").trim();
  return (
    normalizedThreadUuid.length > 0 &&
    !isTempChatThreadUuid(normalizedThreadUuid)
  );
};

const isSubmittedHumanMessage = (message) =>
  message?.sender === "human" && !message?.isButton && hasText(message?.message);

const messageHasChart = (message) =>
  (Array.isArray(message?.dates) && message.dates.length > 0) ||
  (Array.isArray(message?.values1) && message.values1.length > 0) ||
  (Array.isArray(message?.values_1) && message.values_1.length > 0);

const firstCompletedAnswerAfterHuman = (messages = []) => {
  let sawSubmittedQuestion = false;

  for (const message of messages || []) {
    if (isSubmittedHumanMessage(message)) {
      sawSubmittedQuestion = true;
      continue;
    }

    if (sawSubmittedQuestion && message?.sender === "bot" && hasText(message?.message)) {
      return message;
    }
  }

  return null;
};

const isPreAccountUser = (userData = {}) =>
  !userData?.user_id && userData?.uuidHasAccount === false;

const normalizedQuestion = (question) =>
  String(question || "").trim().replace(/\s+/g, " ");

const isPlaceholderThreadTitle = (value) => {
  const title = String(value || "").trim();
  return !title || title === "Chat" || /^New Thread(?: \d+)?$/.test(title);
};

const resolvedThreadTitle = (currentTitle, incomingTitle) => {
  const current = String(currentTitle || "").trim();
  const incoming = String(incomingTitle || "").trim();
  if (
    incoming &&
    !(isPlaceholderThreadTitle(incoming) && current && !isPlaceholderThreadTitle(current))
  ) {
    return incoming;
  }
  return current || incoming || "Chat";
};

const normalizedFollowupButtonQuestions = (message) => {
  const raw =
    message?.followupButtonQuestions ??
    message?.followup_button_questions ??
    [];
  return Array.isArray(raw)
    ? raw
        .map((question) => String(question || "").trim())
        .filter(hasText)
        .slice(0, 2)
    : [];
};

const hasServerBackedMessageIdentity = (message) =>
  message?.id != null ||
  message?.createdAt != null ||
  message?.created_at != null;

const latestPromptButtonsFromMessages = (messages = []) => {
  if (!Array.isArray(messages) || messages.length === 0) {
    return { hasTerminalMessage: false, questions: [] };
  }

  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (
      !message ||
      message.isDeleted ||
      message.is_deleted ||
      message.isError ||
      message.is_error
    ) {
      continue;
    }

    if (!["bot", "human"].includes(message.sender)) continue;
    if (!hasServerBackedMessageIdentity(message)) {
      return { hasTerminalMessage: false, questions: [] };
    }

    if (message.sender === "bot") {
      return {
        hasTerminalMessage: true,
        questions: normalizedFollowupButtonQuestions(message),
      };
    }

    return { hasTerminalMessage: true, questions: [] };
  }

  return { hasTerminalMessage: false, questions: [] };
};

const responseFlowWithPromptButtons = (flow, questions) => {
  const base = flow || {};
  const question1 = Array.isArray(base.question1) ? base.question1 : [];
  const question2 = Array.isArray(base.question2) ? base.question2 : [];
  const [q1, q2] = Array.isArray(questions) ? questions : [];

  return {
    ...base,
    review: true,
    text: true,
    question1: q1
      ? [true, q1, null, false, null, question1[5] || null]
      : [
          false,
          question1[1],
          question1[2],
          question1[3],
          question1[4],
          question1[5],
        ],
    question2: q2
      ? [true, q2, null, false, null, question2[5] || null]
      : [
          false,
          question2[1],
          question2[2],
          question2[3],
          question2[4],
          question2[5],
        ],
    regen: true,
  };
};

const ChatPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    appProps,
    messagesByThreadUuid,
    setMessagesByThreadUuid,
    showPastDueModal,
    setShowPastDueModal,
    setWatchlistData,
    websocketSubscriptionRef,
  } = useAppContext();

  const {
    loadingUserData,
    messageThreads,
    setMessageThreads,
    userData,
    setUserData,
    hasShownHistory,
    allowSetShit,
    setAllowSetShit,
    testVar,
    setTestVar,
    placeholderText,
    setResponseFlow,
    responseFlowByThreadUuid,
    setResponseFlowByThreadUuid,
    freshResponseFlow,
    setRegisterNavMessage,
    setHasAcceptedTerms,
    setShowChatShare,
    isNewUUID,
    showSignUpModal,
    setShowSignUpModal,
    showPaywallModal,
    setShowPaywallModal,
    paywallMessage,
    setPaywallMessage,
    setConversionGateContext,
    inputPlaceholderText,
    setInputPlaceholderText,
    showPaywallAfter,
    setShowPaywallAfter,
    loadingUserMessages,
  } = appProps;

  const activeChatThreadPersistenceScope = useMemo(
    () => ({
      user_id: userData?.user_id,
      uuid: userData?.uuid,
      current_session_id: userData?.current_session_id,
    }),
    [userData?.current_session_id, userData?.user_id, userData?.uuid],
  );

  const {
    isSecondaryExperience,
    loadingResponse,
    showTermsModal,
    setShowTermsModal,
    showAddHomeModal,
    setShowAddHomeModal,
    showSubOfferModal,
    setQuestion,
    preventQuestionInput,
    setPreventQuestionInput,
    preventQuestionInputByThreadUuid = {},
    showLoadingWaitingMessage,
    setShowLoadingWaitingMessage,
    useFullWidth,
    setIsSecondaryExperience,
    showSignInButtons,
    setQuestionOnDemand,
    question,
    setDeviceType,
    setSecondaryExperienceType,
    setSecondaryExperienceMessage,
    setExperienceMessageThreads,
    setUseFullWidth,
    secondaryExperienceMessage,
    setButtonOpacity,
    setPreventScroll,
    setLoadingResponse,
    deviceType,
    setFreeUnlimitedOnly,
    setHasShownFreeUnlimitedMessage,
    checkThing,
    setTestResponseMessage,
    setOpacity,
    setIdSelected,
    // setDataProcessesMessage,
    setLiveTextOutputting,

    showLoadingWaitingMessageByThreadUuid,
    setThreadShowLoadingWaitingMessage,
    setThreadLoadingPhaseMessage,
    setThreadLoadingPhaseDetail,
    pushDataProcessForThread,
    clearDataProcessesForThread,
    pushAnalysisTraceEventForThread,
    setThreadLiveTextOutputting,
    setThreadQuestionOnDemand,
    setThreadLoadingResponse,
    setThreadPreventQuestionInput,
    setThreadThing,
    setThreadTestResponseMessage,
    thingByThreadUuid,
    testResponseMessageByThreadUuid,
    loadingResponseByThreadUuid,
    queuedMessages,
    queuedMessagesByThreadUuid,
    enqueueQueuedMessageForThread,
    removeQueuedMessageForThread,
    markQueuedMessageForThread,
    updateQueuedMessageForThread,
    shiftQueuedMessageForThread,
    moveThreadState,
    activeThread,
    setActiveThread,
    threads,
    setThreads,
    loadingMessages,
    setLoadingMessages,
    isSidebarOpen,
    setIsSidebarOpen,
    isDataProcessesOpen,
    dataProcessesPanelWidth,
    analysisTraceEvents,
    setIsDataProcessesOpen,
    setShowSubOfferModal,
    setAnimatedItems,
    websocketActiveTickerId,
    websocketActiveChartRef,
    loadingMessageThreads,
    scrollToMessageId,
    markThreadUnread,
    markThreadRead,
  } = useChatContext();
  const {
    setLatestTickerPrices,
    setDisplayUpdatedInfo,
  } = useLiveQuoteActions();

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { slug: marketingSlug } = useParams();
  const chatSearchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const querystringQuestion =
    chatSearchParams.get("question")?.trim() || "";
  const requestedChatThreadUuid =
    chatSearchParams.get("thread")?.trim() || "";
  const hasRequestedPersistedChatThread = isPersistedChatThreadUuid(
    requestedChatThreadUuid,
  );
  const freshRequestedChatThread = useMemo(() => {
    if (!hasRequestedPersistedChatThread) return null;
    return readRememberedActiveChatThreadByUuid(
      requestedChatThreadUuid,
    );
  }, [
    hasRequestedPersistedChatThread,
    requestedChatThreadUuid,
  ]);
  const hasFreshRequestedPersistedChatThread = Boolean(
    freshRequestedChatThread,
  );
  const updateRequestedChatThreadInUrl = useCallback(
    (threadUuid) => {
      if (marketingSlug) return;
      const nextParams = new URLSearchParams(location.search);
      const normalizedThreadUuid = String(threadUuid || "").trim();

      if (normalizedThreadUuid) {
        nextParams.set("thread", normalizedThreadUuid);
      } else {
        nextParams.delete("thread");
      }

      const nextSearch = nextParams.toString()
        ? `?${nextParams.toString()}`
        : "";
      if (location.pathname === "/chat" && location.search === nextSearch) {
        return;
      }

      navigate(
        {
          pathname: "/chat",
          search: nextSearch,
        },
        { replace: true },
      );
    },
    [location.pathname, location.search, marketingSlug, navigate],
  );

  useEffect(() => {
    if (!hasRequestedPersistedChatThread) return;
    if (hasFreshRequestedPersistedChatThread) return;
    updateRequestedChatThreadInUrl(null);
  }, [
    hasFreshRequestedPersistedChatThread,
    hasRequestedPersistedChatThread,
    updateRequestedChatThreadInUrl,
  ]);

  const navRef = useRef(null);
  const chatContainerRef = useRef();
  const chatMessagesContainerRef = useRef();
  const chatRef = useRef(null);
  // Set to the real uuid when a temp/new thread resolves mid-conversation so
  // ChatBox can treat the threadUuid change as an in-place resolve (keep scroll
  // position) rather than a thread switch (which resets scroll to the top).
  const pendingResolvedThreadUuidRef = useRef(null);
  const processedSlugRef = useRef(null);
  const processedAutoQuestionRef = useRef(null);
  const lastReadBotIdRef = useRef({});
  const lastReadAtRef = useRef({});
  const [anchorOffsetTop, setAnchorOffsetTop] = useState(0);
  const [editingMessage, setEditingMessage] = useState(null);
  const [cancelRequested, setCancelRequested] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showReferralInviteModal, setShowReferralInviteModal] = useState(false);
  const [referralProgram, setReferralProgram] = useState(null);
  const [showServiceTipModal, setShowServiceTipModal] = useState(false);
  const [serviceTipPrompt, setServiceTipPrompt] = useState(null);
  const [showTextSetupModal, setShowTextSetupModal] = useState(false);
  const [textSetupContext, setTextSetupContext] = useState(null);
  const [paywallConversionGateContext, setPaywallConversionGateContext] =
    useState(null);
  const [rating, setRating] = useState(0);
  const [isGoogleReview, setIsGoogleReview] = useState(false);
  const [showLegacyFirstUserExperience, setShowLegacyFirstUserExperience] =
    useState(false);
  const [activeInAppNotification, setActiveInAppNotification] = useState(null);
  const [proBannerSessionQuestionCount, setProBannerSessionQuestionCount] =
    useState(0);
  const chatDispatchApiRef = useRef(null);
  const inFlightSendByThreadRef = useRef({});
  const queuedMessagesByThreadUuidRef = useRef({});
  const restoredPendingSecondQuestionRef = useRef(null);
  const surfacedInAppNotificationIdsRef = useRef(new Set());

  const sendUserQuestion = useCallback(
    (...args) => chatDispatchApiRef.current?.sendUserQuestion?.(...args) ?? false,
    [],
  );

  const moveQueuedMessagesByThreadUuidRef = useCallback((fromThreadUuid, toThreadUuid) => {
    chatDispatchApiRef.current?.moveQueuedMessagesByThreadUuidRef?.(
      fromThreadUuid,
      toThreadUuid,
    );
  }, []);

  const handleBeginEditMessage = useCallback(
    ({ idx, messageIndex, messageId, previousMessageId, text, messageThread }) => {
      setEditingMessage({
        idx,
        messageIndex: Number.isInteger(messageIndex) ? messageIndex : idx,
        messageId,
        previousMessageId: previousMessageId ?? 0,
        text: text || "",
        messageThread: messageThread || null,
        threadUuid: activeThread?.threadUuid ?? null,
      });
    },
    [activeThread?.threadUuid],
  );

  const handleCancelEdit = useCallback(() => {
    setEditingMessage(null);
  }, []);

  useEffect(() => {
    setEditingMessage(null);
  }, [activeThread?.threadUuid]);

  const handleInputSubmit = (
    q = null,
    messageId = null,
    ticker_ids = null,
    imagesMeta = [],
  ) => {
    const pendingQuestion = q ?? question;
    const firstCompletedAnswer = firstCompletedAnswerAfterHuman(messageThreads);

    if (
      !editingMessage &&
      isPreAccountUser(userData) &&
      firstCompletedAnswer &&
      hasText(pendingQuestion)
    ) {
      const pending = writePendingSecondQuestion({
        question: pendingQuestion,
        threadUuid: activeThread?.threadUuid ?? null,
        currentSessionId: userData?.current_session_id ?? null,
        firstAnswerHadChart: messageHasChart(firstCompletedAnswer),
        firstAnswerFollowupClicked: false,
      });
      if (pending) {
        setQuestion(pending.question);
        setShowSignUpModal(true);
        return false;
      }
    }

    if (editingMessage) return false;
    return sendUserQuestion(q, messageId, ticker_ids, imagesMeta);
  };

  const submitEditedMessage = (newText, imagesMeta = []) => {
    const ctx = editingMessage;
    if (!ctx) {
      return sendUserQuestion(newText, null, null, imagesMeta);
    }

    const editIndex = Number.isInteger(ctx.messageIndex) ? ctx.messageIndex : ctx.idx;
    const orig =
      ctx.messageThread ||
      (Array.isArray(messageThreads) ? messageThreads[editIndex] : null) ||
      {};
    const rawAnchorMessageId = ctx.previousMessageId;
    const messageId =
      editIndex <= 0
        ? 0
        : hasPersistedMessageId(rawAnchorMessageId)
          ? rawAnchorMessageId
          : null;

    const cleaned = (newText ?? "").trim();
    if (!cleaned) {
      return false;
    }

    if (orig.queueStatus) {
      const threadUuid = ctx.threadUuid ?? activeThread?.threadUuid ?? null;
      const targetId =
        orig.clientQueueId ||
        ctx.messageId ||
        orig.id ||
        orig.uuid ||
        null;
      const submitted =
        chatDispatchApiRef.current?.submitEditedQueuedMessageForThread?.(
          threadUuid,
          targetId,
          cleaned,
        ) || false;
      if (submitted) {
        setEditingMessage(null);
      }
      return submitted;
    }

    if (messageId == null) {
      console.warn("Cannot submit edited message without a persisted edit anchor.");
      return false;
    }

    const updated = {
      ...orig,
      id: `edited-${Date.now()}`,
      replacesMessageId: orig.id ?? orig.uuid ?? null,
      message: cleaned,
      attachments:
        Array.isArray(imagesMeta) && imagesMeta.length > 0
          ? filesToAttachments(imagesMeta)
          : Array.isArray(orig.attachments)
            ? orig.attachments
            : [],
      edited: true,
      updatedAt: new Date(),
    };

    setEditingMessage(null);
    if (activeThread?.threadUuid) {
      updateThreadMessages(activeThread.threadUuid, (currentMessages) =>
        Array.isArray(currentMessages) ? currentMessages.slice(0, editIndex) : [],
      );
    } else {
      setMessageThreads((currentMessages) =>
        Array.isArray(currentMessages) ? currentMessages.slice(0, editIndex) : [],
      );
    }

    // Re-run the pipeline from the message before the edited bubble.
    return sendUserQuestion(cleaned, messageId, null, imagesMeta, {
      suppressLocalUserAppend: true,
      overrideMessage: updated,
      editAnchorMessageId: messageId,
    });
  };

  // Measure our nav bar for pinning user messages under it
  useLayoutEffect(() => {
    if (!navRef.current) return;

    const measure = () => {
      const rect = navRef.current.getBoundingClientRect();
      setAnchorOffsetTop((rect && rect.height ? rect.height : 0) + 8); // +8px breathing room
    };

    measure();

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(navRef.current);
    }

    // in case styles/fonts settle after first paint
    const t1 = setTimeout(measure, 0);
    const t2 = setTimeout(measure, 200);
    window.addEventListener("resize", measure);

    return () => {
      if (ro) ro.disconnect();
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener("resize", measure);
    };
  }, []);

  const hasAnalysisTrace =
    userData?.is_admin_user === true &&
    Array.isArray(analysisTraceEvents) &&
    analysisTraceEvents.length > 0;
  const chatLayout = useChatLayout({
    isSidebarOpen,
    isDataProcessesOpen,
    hasAnalysisTrace,
    dataProcessesWidth: dataProcessesPanelWidth,
  });
  const sidebarOffset = chatLayout.sidebarOffset;

  const lastSessionIdRef = useRef(null);
  const activeThreadRef = useRef(null);
  const messagesByThreadUuidRef = useRef(messagesByThreadUuid || {});
  const loadedMessagesByThreadUuidRef = useRef({});
  const responseFlowByThreadUuidRef = useRef(responseFlowByThreadUuid || {});
  const messageThreadsOwnerRef = useRef(null);
  const resolvedTempThreadMapRef = useRef({});
  const resolveTempIdRef = useRef(null);
  const threadSelectionTokenRef = useRef(0);
  const [skipHideThreadUuid, setSkipHideThreadUuid] = useState(null);
  const threadPrefetchInFlightRef = useRef(new Map());
  const hydratedMessagesByThreadUuidRef = useRef({});
  const threadHydrationInFlightRef = useRef(new Map());
  const shouldForceFreshLoadThreadRef = useRef(false);
  const hasForcedFreshLoadThreadRef = useRef(false);
  const returningBootstrapAttemptedRef = useRef(false);
  const returningBootstrapInFlightRef = useRef(false);
  const firstUserBootstrapAttemptedRef = useRef(false);
  const firstUserBootstrapInFlightRef = useRef(false);
  useEffect(() => {
    returningBootstrapAttemptedRef.current = false;
    returningBootstrapInFlightRef.current = false;
  }, [userData?.user_id]);

  useEffect(() => {
    firstUserBootstrapAttemptedRef.current = false;
    firstUserBootstrapInFlightRef.current = false;
    setShowLegacyFirstUserExperience(false);
  }, [userData?.current_session_id, userData?.user_id]);

  useEffect(() => {
    setProBannerSessionQuestionCount(0);
  }, [userData?.current_session_id]);

  useEffect(() => {
    messagesByThreadUuidRef.current = messagesByThreadUuid || {};
  }, [messagesByThreadUuid]);

  useEffect(() => {
    queuedMessagesByThreadUuidRef.current = queuedMessagesByThreadUuid || {};
  }, [queuedMessagesByThreadUuid]);

  useEffect(() => {
    responseFlowByThreadUuidRef.current = responseFlowByThreadUuid || {};
  }, [responseFlowByThreadUuid]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.__chatFreshLoadThreadInitialized) return;
    window.__chatFreshLoadThreadInitialized = true;
    shouldForceFreshLoadThreadRef.current = true;
  }, []);

  const updateThreadMessages = useCallback(
    (threadUuid, updater) => {
      if (!threadUuid) return;
      const currentMap = messagesByThreadUuidRef.current || {};
      const current = Array.isArray(currentMap?.[threadUuid])
        ? currentMap[threadUuid]
        : [];
      const next = typeof updater === "function" ? updater(current) : updater;
      const nextMap = { ...(currentMap || {}), [threadUuid]: next };
      messagesByThreadUuidRef.current = nextMap;
      setMessagesByThreadUuid(nextMap);
      if (activeThreadRef.current?.threadUuid === threadUuid) {
        messageThreadsOwnerRef.current = threadUuid;
        setMessageThreads(next);
      }
    },
    [setMessagesByThreadUuid, setMessageThreads],
  );

  const setResponseFlowForThread = useCallback(
    (threadUuid, updater) => {
      if (!threadUuid) return;
      const currentMap = responseFlowByThreadUuidRef.current || {};
      const current = currentMap?.[threadUuid] || freshResponseFlow();
      const next = typeof updater === "function" ? updater(current) : updater;
      const nextMap = { ...(currentMap || {}), [threadUuid]: next };
      responseFlowByThreadUuidRef.current = nextMap;
      setResponseFlowByThreadUuid(nextMap);
      if (activeThreadRef.current?.threadUuid === threadUuid) {
        setResponseFlow(next);
      }
    },
    [setResponseFlowByThreadUuid, setResponseFlow, freshResponseFlow],
  );

  const ensureResponseFlowForThread = useCallback(
    (threadUuid, options = {}) => {
      if (!threadUuid) return null;
      const existing = responseFlowByThreadUuidRef.current?.[threadUuid];
      if (existing) return existing;
      const shouldHideQuestionButtons = Boolean(options?.hideQuestionButtons);
      const fresh = shouldHideQuestionButtons
        ? hideQuestionButtonsForFlow(freshResponseFlow())
        : freshResponseFlow();
      const currentMap = responseFlowByThreadUuidRef.current || {};
      const nextMap = { ...(currentMap || {}), [threadUuid]: fresh };
      responseFlowByThreadUuidRef.current = nextMap;
      setResponseFlowByThreadUuid(nextMap);
      return fresh;
    },
    [setResponseFlowByThreadUuid, freshResponseFlow],
  );

  const moveMessagesByThreadUuid = useCallback(
    (fromThreadUuid, toThreadUuid) => {
      if (!fromThreadUuid || !toThreadUuid || fromThreadUuid === toThreadUuid)
        return;
      const currentMap = messagesByThreadUuidRef.current || {};
      if (!Object.prototype.hasOwnProperty.call(currentMap, fromThreadUuid))
        return;
      const next = moveThreadMessages(currentMap, fromThreadUuid, toThreadUuid);
      messagesByThreadUuidRef.current = next;
      setMessagesByThreadUuid(next);
    },
    [setMessagesByThreadUuid],
  );

  const moveResponseFlowByThreadUuid = useCallback(
    (fromThreadUuid, toThreadUuid) => {
      if (!fromThreadUuid || !toThreadUuid || fromThreadUuid === toThreadUuid)
        return;
      const currentMap = responseFlowByThreadUuidRef.current || {};
      if (!Object.prototype.hasOwnProperty.call(currentMap, fromThreadUuid))
        return;
      const next = { ...(currentMap || {}) };
      next[toThreadUuid] = next[fromThreadUuid];
      delete next[fromThreadUuid];
      responseFlowByThreadUuidRef.current = next;
      setResponseFlowByThreadUuid(next);
    },
    [setResponseFlowByThreadUuid],
  );

  const syncPromptButtonsForThread = useCallback(
    (threadUuid, messages) => {
      if (!threadUuid || !Array.isArray(messages) || messages.length === 0) {
        return false;
      }

      const latestPromptButtons = latestPromptButtonsFromMessages(messages);
      if (!latestPromptButtons.hasTerminalMessage) return false;

      setResponseFlowForThread(threadUuid, (flow) => {
        const baseFlow = flow || freshResponseFlow();
        if (latestPromptButtons.questions.length > 0) {
          return responseFlowWithPromptButtons(
            baseFlow,
            latestPromptButtons.questions,
          );
        }

        return hideQuestionButtonsForFlow(baseFlow);
      });

      return true;
    },
    [freshResponseFlow, setResponseFlowForThread],
  );

  const {
    ensureThreadSubscription,
    moveThreadSubscription,
  } = useChatThreadSubscriptions({
    activeThread,
    activeThreadRef,
    loadingResponseByThreadUuid,
    pushAnalysisTraceEventForThread,
    pushDataProcessForThread,
    setAnimatedItems,
    setDisplayUpdatedInfo,
    setLatestTickerPrices,
    setThreadLiveTextOutputting,
    setThreadLoadingPhaseDetail,
    setThreadLoadingPhaseMessage,
    setThreadQuestionOnDemand,
    setThreadShowLoadingWaitingMessage,
    setUseFullWidth,
    setWatchlistData,
    showLoadingWaitingMessageByThreadUuid,
    updateThreadMessages,
    useFullWidth,
    userSessionId: userData?.current_session_id,
    websocketActiveChartRef,
    websocketActiveTickerId,
    websocketSubscriptionRef,
  });

  const {
    hydrateThreadMessages,
    prefetchThreadMessages,
    selectActiveThread,
  } = useChatThreadNavigation({
    activeChatThreadPersistenceScope,
    activeThreadRef,
    ensureResponseFlowForThread,
    ensureThreadSubscription,
    freshRequestedChatThread,
    freshResponseFlow,
    hasFreshRequestedPersistedChatThread,
    hydratedMessagesByThreadUuidRef,
    loadedMessagesByThreadUuidRef,
    loadingMessageThreads,
    loadingUserData,
    messageThreadsOwnerRef,
    messagesByThreadUuidRef,
    requestedChatThreadUuid,
    scrollToMessageId,
    setActiveThread,
    setLoadingMessages,
    setMessageThreads,
    setResponseFlow,
    setSkipHideThreadUuid,
    threadHydrationInFlightRef,
    threadPrefetchInFlightRef,
    threadSelectionTokenRef,
    threads,
    updateRequestedChatThreadInUrl,
    updateThreadMessages,
    userId: userData?.user_id,
  });

  const {
    adoptPendingSignupQuestion,
    clearPendingSignupComposer,
    completePendingSignupQuestion,
    handlePendingSignupQuestionEvent,
    isActivePendingSignupStatus,
  } = useChatPendingSignup({
    activeChatThreadPersistenceScope,
    activeThread,
    activeThreadRef,
    clearDataProcessesForThread,
    ensureThreadSubscription,
    freshResponseFlow,
    hideQuestionButtonsForFlow,
    hydrateThreadMessages,
    restoredPendingSecondQuestionRef,
    setActiveThread,
    setButtonOpacity,
    setPreventScroll,
    setQuestion,
    setResponseFlowForThread,
    setThreadLiveTextOutputting,
    setThreadLoadingPhaseDetail,
    setThreadLoadingPhaseMessage,
    setThreadLoadingResponse,
    setThreadPreventQuestionInput,
    setThreadQuestionOnDemand,
    setThreadShowLoadingWaitingMessage,
    setThreadTestResponseMessage,
    setThreadThing,
    threads,
    updateThreadMessages,
    websocketSubscriptionRef,
  });

  useEffect(() => {
    setIsSidebarOpen(isDesktop ? true : false);
    setIsDataProcessesOpen(isDesktop ? true : false);
  }, [isDesktop]);

  useEffect(() => {
    const curId = 2;

    if (!curId || loadingUserData) {
      return;
    }

    if (lastSessionIdRef.current === null) {
      lastSessionIdRef.current = curId;
      return;
    }

    if (curId !== lastSessionIdRef.current) {
      handleAddThread();
      lastSessionIdRef.current = curId;
    }
  }, [userData?.current_session_id, loadingUserData]);

  useEffect(() => {
    activeThreadRef.current = activeThread;
  }, [activeThread]);

  useEffect(() => {
    if (!showPaywallModal) {
      setPaywallConversionGateContext(null);
    }
  }, [showPaywallModal]);

  useEffect(() => {
    if (!activeThread?.threadUuid) return;
    if (
      isTempChatThreadUuid(activeThread.threadUuid) &&
      !activeThread.pendingRealThread
    ) {
      return;
    }
    rememberActiveChatThread(activeThread, activeChatThreadPersistenceScope);
  }, [activeChatThreadPersistenceScope, activeThread]);

  useEffect(() => {
    const threadUuid = activeThread?.threadUuid;
    if (!isPersistedChatThreadUuid(threadUuid)) return;
    updateRequestedChatThreadInUrl(threadUuid);
  }, [activeThread?.threadUuid, updateRequestedChatThreadInUrl]);

  useEffect(() => {
    const threadUuid = activeThread?.threadUuid ?? null;
    if (!threadUuid) return;
    const cachedMessages = messagesByThreadUuidRef.current?.[threadUuid];
    if (
      !loadingResponseByThreadUuid?.[threadUuid] &&
      syncPromptButtonsForThread(threadUuid, cachedMessages)
    ) {
      return;
    }
    const existing = responseFlowByThreadUuidRef.current?.[threadUuid];
    const flow = existing || ensureResponseFlowForThread(threadUuid);
    if (flow) setResponseFlow(flow);
  }, [
    activeThread?.threadUuid,
    ensureResponseFlowForThread,
    loadingResponseByThreadUuid,
    setResponseFlow,
    syncPromptButtonsForThread,
  ]);

  useEffect(() => {
    const threadUuid = activeThread?.threadUuid ?? null;
    if (!threadUuid || loadingResponseByThreadUuid?.[threadUuid]) return;
    const cachedMessages = messagesByThreadUuidRef.current?.[threadUuid];
    const sourceMessages = Array.isArray(cachedMessages)
      ? cachedMessages
      : messageThreadsOwnerRef.current === threadUuid
        ? messageThreads
        : null;
    syncPromptButtonsForThread(threadUuid, sourceMessages);
  }, [
    activeThread?.threadUuid,
    loadingResponseByThreadUuid,
    messageThreads,
    syncPromptButtonsForThread,
  ]);

  const markThreadReadNow = useCallback(
    (threadUuid, options = {}) => {
      if (!threadUuid || threadUuid.startsWith("temp-")) return;
      const { botMessageId = null } = options || {};
      const now = Date.now();
      lastReadAtRef.current = {
        ...(lastReadAtRef.current || {}),
        [threadUuid]: Math.max(
          Number(lastReadAtRef.current?.[threadUuid]) || 0,
          now,
        ),
      };
      if (botMessageId != null) {
        const nextId = Number(botMessageId);
        if (Number.isFinite(nextId)) {
          lastReadBotIdRef.current = {
            ...(lastReadBotIdRef.current || {}),
            [threadUuid]: Math.max(
              Number(lastReadBotIdRef.current?.[threadUuid]) || 0,
              nextId,
            ),
          };
        }
      }
      markThreadRead(threadUuid);
      markChatThreadRead(threadUuid)
        .then((res) => {
          const serverMs = Date.parse(res?.lastReadAt || "");
          if (!Number.isFinite(serverMs)) return;
          lastReadAtRef.current = {
            ...(lastReadAtRef.current || {}),
            [threadUuid]: Math.max(
              Number(lastReadAtRef.current?.[threadUuid]) || 0,
              serverMs,
            ),
          };
        })
        .catch(() => {});
    },
    [markThreadRead, markChatThreadRead],
  );

  useEffect(() => {
    const threadUuid = activeThread?.threadUuid;
    if (!threadUuid || threadUuid.startsWith("temp-")) return;
    markThreadReadNow(threadUuid);
  }, [activeThread?.threadUuid, markThreadReadNow]);

  useEffect(() => {
    const threadUuid = activeThread?.threadUuid;
    if (!threadUuid || threadUuid.startsWith("temp-")) return;
    if (!messageThreads || messageThreads.length === 0) return;
    const last = messageThreads[messageThreads.length - 1];
    if (!last || last.sender !== "bot") return;
    if (!last.id) return;
    const seenId = Number(lastReadBotIdRef.current?.[threadUuid]);
    const nextId = Number(last.id);
    if (Number.isFinite(seenId) && Number.isFinite(nextId) && seenId >= nextId)
      return;
    markThreadReadNow(threadUuid, { botMessageId: last.id });
  }, [activeThread?.threadUuid, messageThreads, markThreadReadNow]);

  useEffect(() => {
    if (loadingUserData) return;
    // Subscribe based on either uuid (pre-account) or user_id (logged-in)
    if (!userData?.uuid && !userData?.user_id) return;
    // Drop a socket left over from a previous account before subscribing, so we
    // never listen as one identity while sending as another.
    syncCableIdentity(userData?.user_id ?? userData?.uuid);
    const cleanup = connectUserThreadUpdates({
      setThreads,
      setMessageThreads,
      activeThreadRef,
      setActiveThread,
      markThreadUnread,
      markThreadRead,
      getLastReadAt: (uuid) => lastReadAtRef.current?.[uuid],
      getLastReadBotId: (uuid) => lastReadBotIdRef.current?.[uuid],
      onPendingSignupQuestion: handlePendingSignupQuestionEvent,
    });
    return cleanup;
  }, [
    loadingUserData,
    userData?.uuid,
    userData?.user_id,
    markThreadUnread,
    markThreadRead,
    handlePendingSignupQuestionEvent,
  ]);

  const hasBlockingOverlay = Boolean(
    showTermsModal ||
      showAddHomeModal ||
      showSignUpModal ||
      showPaywallModal ||
      showPastDueModal ||
      showReviewModal ||
      showReferralInviteModal ||
      showServiceTipModal ||
      showTextSetupModal ||
      showSubOfferModal === "offering"
  );

  const surfaceInAppNotification = useCallback(
    async (notification) => {
      if (!notification?.id) return false;
      if (notification?.seenAt) return false;
      if (!isInAppModalNotification(notification)) return false;
      if (hasBlockingOverlay || activeInAppNotification) return false;

      const notificationId = String(notification.id);
      if (surfacedInAppNotificationIdsRef.current.has(notificationId)) {
        return false;
      }

      surfacedInAppNotificationIdsRef.current.add(notificationId);
      setActiveInAppNotification(notification);

      try {
        await markNotificationSeen(notification.id);
        const seenAt = new Date().toISOString();
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("user-notifications:updated", {
              detail: {
                notification: {
                  ...notification,
                  seenAt,
                  readAt: notification?.readAt || seenAt,
                },
              },
            })
          );
        }
      } catch (error) {
        console.error("mark in-app notification seen failed", error);
      }

      return true;
    },
    [activeInAppNotification, hasBlockingOverlay]
  );

  const refreshUnreadInAppNotifications = useCallback(async () => {
    if (loadingUserData) return;
    if (!userData?.user_id) return;
    if (hasBlockingOverlay || activeInAppNotification) return;

    const page = await getUserNotificationsPage({
      limit: 20,
      offset: 0,
      unreadOnly: true,
    });

    if (!Array.isArray(page)) return;

    const nextNotification = page.find(
      (notification) =>
        !notification?.seenAt &&
        isInAppModalNotification(notification) &&
        !surfacedInAppNotificationIdsRef.current.has(String(notification.id))
    );

    if (nextNotification) {
      await surfaceInAppNotification(nextNotification);
    }
  }, [
    activeInAppNotification,
    hasBlockingOverlay,
    loadingUserData,
    surfaceInAppNotification,
    userData?.user_id,
  ]);

  useEffect(() => {
    surfacedInAppNotificationIdsRef.current = new Set();
    setActiveInAppNotification(null);
  }, [userData?.user_id]);

  useEffect(() => {
    if (loadingUserData || !userData?.user_id) return undefined;

    refreshUnreadInAppNotifications();
    const intervalId = setInterval(refreshUnreadInAppNotifications, 30_000);
    return () => clearInterval(intervalId);
  }, [
    loadingUserData,
    refreshUnreadInAppNotifications,
    userData?.user_id,
  ]);

  useEffect(() => {
    if (hasBlockingOverlay || activeInAppNotification) return;
    refreshUnreadInAppNotifications();
  }, [
    activeInAppNotification,
    hasBlockingOverlay,
    refreshUnreadInAppNotifications,
  ]);

  useEffect(() => {
    const handleUserNotificationUpdate = (event) => {
      const nextNotification = event?.detail?.notification;
      if (!nextNotification) return;
      surfaceInAppNotification(nextNotification);
    };

    window.addEventListener(
      "user-notifications:updated",
      handleUserNotificationUpdate
    );
    return () =>
      window.removeEventListener(
        "user-notifications:updated",
        handleUserNotificationUpdate
      );
  }, [surfaceInAppNotification]);

  // WS-driven prepop questions (Phase 3-extension). The backend broadcasts
  // questions as soon as the parallel follow-up LLM thread finishes, often
  // before the HTTP response lands. Push them into responseFlow directly so
  // the buttons appear with the chart instead of popping in late. The HTTP
  // response handler later re-sets the same fields — no-op visually.
  useEffect(() => {
    const handleQuestionsPrepop = (event) => {
      const detail = event?.detail || {};
      const threadUuid = detail.threadUuid || null;
      const questions = Array.isArray(detail.questions) ? detail.questions : [];
      if (questions.length === 0) return;
      const [q1, q2] = questions;
      const apply = (flow) => ({
        ...flow,
        ...(q1
          ? { question1: [true, q1, null, false, null, null] }
          : {}),
        ...(q2
          ? { question2: [true, q2, null, false, null, null] }
          : {}),
      });
      if (threadUuid) {
        setResponseFlowForThread(threadUuid, apply);
      } else {
        setResponseFlow(apply);
      }
    };
    window.addEventListener("incite:questions-prepop", handleQuestionsPrepop);
    return () =>
      window.removeEventListener(
        "incite:questions-prepop",
        handleQuestionsPrepop,
      );
  }, [setResponseFlowForThread, setResponseFlow]);

  useEffect(() => {
    const d = getDeviceType();
    setDeviceType(d);
  }, []);

  const handleWindowScroll = useCallback((e) => {
    setTestVar(window.pageYOffset);
  }, []);
  useEffect(() => {
    window.addEventListener("scroll", handleWindowScroll, true);
  }, [handleWindowScroll]);

  const isLoggedIn = !!userData.user_id;
  const isAdminUser = userData?.is_admin_user === true;
  const [adminChatModelCatalog, setAdminChatModelCatalog] = useState(null);
  const [adminChatModelCatalogLoading, setAdminChatModelCatalogLoading] = useState(false);
  const [adminChatModelCatalogError, setAdminChatModelCatalogError] = useState(null);
  const [adminBaseDirectiveModelOverride, setAdminBaseDirectiveModelOverride] = useState(() => {
    if (typeof window === "undefined") return "";

    try {
      return window.localStorage.getItem(ADMIN_BASE_DIRECTIVE_MODEL_STORAGE_KEY) || "";
    } catch (_error) {
      return "";
    }
  });
  const [adminBaseDirectiveReasoningOverride, setAdminBaseDirectiveReasoningOverride] = useState(() => {
    if (typeof window === "undefined") return "";

    try {
      return window.localStorage.getItem(ADMIN_BASE_DIRECTIVE_REASONING_STORAGE_KEY) || "";
    } catch (_error) {
      return "";
    }
  });

  useEffect(() => {
    if (!isAdminUser) {
      setAdminChatModelCatalog(null);
      setAdminChatModelCatalogError(null);
      return undefined;
    }

    let cancelled = false;
    setAdminChatModelCatalogLoading(true);
    setAdminChatModelCatalogError(null);
    getAdminChatModelCatalog()
      .then((catalog) => {
        if (cancelled) return;
        setAdminChatModelCatalog(catalog);
      })
      .catch((error) => {
        if (cancelled) return;
        setAdminChatModelCatalogError(error);
      })
      .finally(() => {
        if (!cancelled) setAdminChatModelCatalogLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAdminUser]);

  useEffect(() => {
    if (!isAdminUser || !adminBaseDirectiveModelOverride) return;

    const modelNames = new Set(
      Array.isArray(adminChatModelCatalog?.models)
        ? adminChatModelCatalog.models.map((model) => model?.model_name)
        : [],
    );
    if (modelNames.size === 0 || modelNames.has(adminBaseDirectiveModelOverride)) return;

    setAdminBaseDirectiveModelOverride("");
  }, [adminBaseDirectiveModelOverride, adminChatModelCatalog, isAdminUser]);

  useEffect(() => {
    if (!isAdminUser || !adminBaseDirectiveReasoningOverride) return;

    const models = Array.isArray(adminChatModelCatalog?.models) ? adminChatModelCatalog.models : [];
    if (models.length === 0) return;

    const effectiveModelName = adminBaseDirectiveModelOverride || adminChatModelCatalog?.default_model_name;
    const effectiveModel = models.find((model) => model?.model_name === effectiveModelName);
    const rawOptions =
      effectiveModel?.reasoning_efforts ||
      effectiveModel?.reasoningEfforts ||
      effectiveModel?.reasoning_effort_options ||
      effectiveModel?.reasoningEffortOptions ||
      [];
    const supportedEfforts = new Set(rawOptions.map((option) => (
      typeof option === "string" ? option : option?.value
    )));
    if (supportedEfforts.has(adminBaseDirectiveReasoningOverride)) return;

    setAdminBaseDirectiveReasoningOverride("");
    try {
      window.localStorage.removeItem(ADMIN_BASE_DIRECTIVE_REASONING_STORAGE_KEY);
    } catch (_error) {
      return;
    }
  }, [adminBaseDirectiveModelOverride, adminBaseDirectiveReasoningOverride, adminChatModelCatalog, isAdminUser]);

  const handleAdminBaseDirectiveModelChange = useCallback((modelName) => {
    const nextModelName = String(modelName || "").trim();
    setAdminBaseDirectiveModelOverride(nextModelName);

    if (typeof window === "undefined") return;
    try {
      if (nextModelName) {
        window.localStorage.setItem(ADMIN_BASE_DIRECTIVE_MODEL_STORAGE_KEY, nextModelName);
      } else {
        window.localStorage.removeItem(ADMIN_BASE_DIRECTIVE_MODEL_STORAGE_KEY);
      }
    } catch (_error) {
      return;
    }
  }, []);

  const handleAdminBaseDirectiveReasoningChange = useCallback((reasoningEffort) => {
    const nextReasoningEffort = String(reasoningEffort || "").trim();
    setAdminBaseDirectiveReasoningOverride(nextReasoningEffort);

    if (typeof window === "undefined") return;
    try {
      if (nextReasoningEffort) {
        window.localStorage.setItem(ADMIN_BASE_DIRECTIVE_REASONING_STORAGE_KEY, nextReasoningEffort);
      } else {
        window.localStorage.removeItem(ADMIN_BASE_DIRECTIVE_REASONING_STORAGE_KEY);
      }
    } catch (_error) {
      return;
    }
  }, []);

  const onFocus = (e) => {
    if (!isLoggedIn) {
      // appProps.setRegisterNavMessage(requireLoginMessage)
      // navigate('/register')
    } else {
      setTimeout(function () {
        setAllowSetShit(true);
      }, 150); //magic number is 150
    }
  };

  useEffect(() => {
    if (!isLoggedIn) return;

    const serverPending = userData?.pending_signup_question;
    const serverPendingStatus = String(serverPending?.status || "");
    if (
      serverPending?.question &&
      isActivePendingSignupStatus(serverPendingStatus)
    ) {
      adoptPendingSignupQuestion(serverPending);
      clearPendingSignupComposer(serverPending, { force: true });
      clearPendingSecondQuestion();
      return;
    }
    if (
      serverPending?.question &&
      ["completed", "failed"].includes(serverPendingStatus)
    ) {
      completePendingSignupQuestion(serverPending);
      clearPendingSignupComposer(serverPending);
      clearPendingSecondQuestion();
      return;
    }

    const pending = readPendingSecondQuestion();
    if (!pending?.question) return;

    const currentQuestion = normalizedQuestion(question);
    const pendingQuestion = normalizedQuestion(pending.question);
    if (!currentQuestion || currentQuestion === pendingQuestion) {
      setQuestion(pending.question);
      restoredPendingSecondQuestionRef.current = pending;
      trackFrontendEvent(
        "pending_second_question_restored",
        pendingSecondQuestionEventMetadata({}, pending),
      );
      clearPendingSecondQuestion();
    }
  }, [
    adoptPendingSignupQuestion,
    clearPendingSignupComposer,
    completePendingSignupQuestion,
    isActivePendingSignupStatus,
    isLoggedIn,
    question,
    setQuestion,
    userData?.pending_signup_question,
  ]);

  useEffect(() => {
    if (!querystringQuestion) return;
    if (processedAutoQuestionRef.current === querystringQuestion) return;

    const threadUuid = activeThread?.threadUuid ?? null;
    const isQuestionInputPrevented = threadUuid
      ? Boolean(preventQuestionInputByThreadUuid?.[threadUuid])
      : preventQuestionInput;
    const ready =
      !loadingUserData &&
      Object.keys(userData || {}).length > 0 &&
      !loadingUserMessages &&
      !loadingMessages &&
      Boolean(activeThread) &&
      !isQuestionInputPrevented;

    if (!ready) return;

    let shouldAsk = true;
    if (messageThreads.length > 1) {
      const lastQs = messageThreads.filter((m) => m.sender === "human");
      if (lastQs.length > 0) {
        const lastQ = lastQs[lastQs.length - 1];
        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
        if (
          lastQ.message === querystringQuestion &&
          new Date(lastQ.createdAt) > oneHourAgo
        ) {
          shouldAsk = false;
        }
      }
    }

    if (shouldAsk) {
      const submitted = handleInputSubmit(querystringQuestion);
      if (!submitted) return;
    }

    processedAutoQuestionRef.current = querystringQuestion;

    const nextParams = new URLSearchParams(location.search);
    nextParams.delete("question");
    navigate(
      {
        pathname: location.pathname,
        search: nextParams.toString() ? `?${nextParams.toString()}` : "",
      },
      { replace: true },
    );
  }, [
    querystringQuestion,
    loadingUserData,
    loadingUserMessages,
    loadingMessages,
    userData,
    messageThreads,
    activeThread,
    preventQuestionInput,
    preventQuestionInputByThreadUuid,
    handleInputSubmit,
    location.pathname,
    location.search,
    navigate,
  ]);

  useEffect(() => {
    if (!marketingSlug) return;
    if (processedSlugRef.current === marketingSlug) return;

    // mirror your existing gating to avoid running too early
    const ready =
      !loadingUserData &&
      Object.keys(userData || {}).length > 0 &&
      !loadingUserMessages &&
      !loadingMessages &&
      (activeThread || userData.uuidHasAccount == false);

    if (!ready) return;

    let aborted = false;

    (async () => {
      try {
        const res = await fetch("/graphql", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            query: `{ marketingLink(slug: "${marketingSlug}") { query slug } }`,
          }),
        });
        const json = await res.json();
        const link = json?.data?.marketingLink;
        const q = link?.query;
        if (!q || aborted) return;

        // Avoid auto-sending duplicates (same as your querystring guard)
        let shouldAsk = true;
        if (messageThreads.length > 1) {
          const lastQs = messageThreads.filter((m) => m.sender === "human");
          if (lastQs.length > 0) {
            const lastQ = lastQs[lastQs.length - 1];
            const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
            if (lastQ.message === q && new Date(lastQ.createdAt) > oneHourAgo) {
              shouldAsk = false;
            }
          }
        }

        if (shouldAsk) {
          handleInputSubmit(q);
          // Optional: move user to /chat after firing
          // navigate('/chat', { replace: true });
        }
      } catch (e) {
        console.error("marketing slug fetch failed", e);
      } finally {
        processedSlugRef.current = marketingSlug;
        navigate("/chat", { replace: true }); // remove slug from URL so it can’t trigger again
      }
    })();

    return () => {
      aborted = true;
    };
  }, [
    marketingSlug,
    loadingUserData,
    loadingUserMessages,
    loadingMessages,
    userData,
    messageThreads,
    activeThread,
  ]);

  useChatBootstrapThreads({
    activeChatThreadPersistenceScope,
    activeThread,
    activeThreadRef,
    ensureThreadSubscription,
    firstUserBootstrapAttemptedRef,
    firstUserBootstrapInFlightRef,
    freshResponseFlow,
    hasFreshRequestedPersistedChatThread,
    hydrateThreadMessages,
    loadingMessageThreads,
    loadingUserData,
    messageThreads,
    messageThreadsOwnerRef,
    processedAutoQuestionRef,
    querystringQuestion,
    resolvedTempThreadMapRef,
    resolveTempIdRef,
    returningBootstrapAttemptedRef,
    returningBootstrapInFlightRef,
    selectActiveThread,
    setInputPlaceholderText,
    setIsSecondaryExperience,
    setResponseFlowForThread,
    setSecondaryExperienceMessage,
    setSecondaryExperienceType,
    setShowLegacyFirstUserExperience,
    setThreadLiveTextOutputting,
    setThreadLoadingPhaseDetail,
    setThreadLoadingPhaseMessage,
    setThreadLoadingResponse,
    setThreadPreventQuestionInput,
    setThreadShowLoadingWaitingMessage,
    setThreads,
    updateThreadMessages,
    userData,
  });

  // also add a dependency to message threads..
  // so that any time the message threads is updated, we update the larger
  // messagesByThreadUuid...
  useEffect(() => {
    const threadUuid = activeThread?.threadUuid;
    if (!threadUuid) return;
    if (
      messageThreadsOwnerRef.current &&
      messageThreadsOwnerRef.current !== threadUuid
    ) {
      return;
    }
    const currentMap = messagesByThreadUuidRef.current || {};
    const nextMap = {
      ...(currentMap || {}),
      [threadUuid]: messageThreads || [],
    };
    messagesByThreadUuidRef.current = nextMap;
    setMessagesByThreadUuid(nextMap);
  }, [messageThreads, activeThread?.threadUuid, setMessagesByThreadUuid]);

  useEffect(() => {
    const hasAutoQuestionParam = Boolean(
      new URLSearchParams(location.search).get("question")?.trim(),
    );
    const shouldUseFirstUserPlaceholder =
      !hasAutoQuestionParam &&
      !isLoggedIn &&
      messageThreads.length <= 1 &&
      !(messageThreads[0] && messageThreads[0].sender == "human");

    if (loadingUserData) {
      setIsSecondaryExperience(null);
    } else {
      if (
        showLegacyFirstUserExperience &&
        shouldUseFirstUserPlaceholder
      ) {
        setIsSecondaryExperience(true);
        setSecondaryExperienceType("new-user");
        setSecondaryExperienceMessage(firstUserMessage);
      } else if (userData.showAskShareMessage) {
        // replace with condition for ask-share!
        setIsSecondaryExperience(true);
        setSecondaryExperienceType("ask-share");
        setSecondaryExperienceMessage(askShareMessage);
      } else {
        setSecondaryExperienceType(null);
        setIsSecondaryExperience(false);
        setInputPlaceholderText(
          shouldUseFirstUserPlaceholder
            ? firstUserPlaceholderText
            : startPlaceholderText,
        );
      }
    }
  }, [
    loadingUserData,
    userData,
    messageThreads,
    location.search,
    showLegacyFirstUserExperience,
  ]);

  useEffect(() => {
    if (isSecondaryExperience != null) {
      if (isSecondaryExperience) {
        setExperienceMessageThreads([{ sender: "bot", message: "" }]);
        setTimeout(() => {
          const characters = secondaryExperienceMessage.split("");
          let curStr = "";
          characters.forEach((char, index) => {
            setTimeout(() => {
              curStr = curStr.concat(char);
              setExperienceMessageThreads([
                {
                  sender: "bot",
                  message: curStr,
                },
              ]);
            }, index * 20); //30  // THIS is how quickly the letters print out
          });
        }, 500);

        setTimeout(() => {
          const characters = firstUserPlaceholderText.split("");
          let curStr = "";
          characters.forEach((char, index) => {
            setTimeout(() => {
              curStr = curStr.concat(char);
              setInputPlaceholderText(curStr);
            }, index * 33); //50  // THIS is how quickly the letters print out
          });
        }, 3100); //4600)
      } else if (messageThreads.length == 1) {
        // setMessageThreads([chatStartingResponse]);
        // setInputPlaceholderText(placeholderText);
      }
    }
  }, [isSecondaryExperience]);

  useEffect(() => {
    if (navRef.current && allowSetShit) {
      navRef.current.style.top = `${testVar}px`;
      // Only if there are messages making up less than 50% of the scroll area to fill do we want to move them down; otherwise the keyboard
      // comes up and the messages move up naturally (not behind header).
      // if (chatMessagesContainerRef.current.scrollHeight < .5*chatContainerRef.current.scrollHeight) {
      if (
        messageThreads.length < 2 &&
        testVar &&
        chatRef.current &&
        chatRef.current.style
      ) {
        chatRef.current.style.top = `${testVar - 100}px`;
      }
      setTimeout(function () {
        setAllowSetShit(false);
      }, 150);
    }
  }, [allowSetShit, testVar]);

  const onBlur = (e) => {
    setTimeout(function () {
      if (navRef.current && chatRef.current && chatRef.current.style) {
        navRef.current.style.top = "0px";
        chatRef.current.style.top = `0px`;
      }
      setAllowSetShit(false);
    }, 150);
  };

  const regenButtonRecommendations = async (question = null) => {
    const threadUuid = activeThread?.threadUuid ?? null;
    const responseRecommendations = await regenerateQuestionRecommendations(
      userData,
      appProps,
      question,
    );
    const questions_prepopulated = responseRecommendations.messages;
    const questions_prepopulated_tickers = responseRecommendations.tickers;
    let obj = [];
    let tickers;
    questions_prepopulated.forEach((message_text, idx) => {
      tickers =
        questions_prepopulated_tickers[idx] == ""
          ? ""
          : JSON.parse(questions_prepopulated_tickers[idx]);
      let q = {
        sender: "human",
        isButton: true,
        message: message_text,
        tickers: tickers,
      };
      if (idx == 0) {
        if (threadUuid) {
          setResponseFlowForThread(threadUuid, (responseFlow) => ({
            ...responseFlow,
            question1: [true, q.message, q.tickers, false, ""],
          }));
        } else {
          setResponseFlow((responseFlow) => ({
            ...responseFlow,
            question1: [true, q.message, q.tickers, false, ""],
          }));
        }
      } else {
        if (threadUuid) {
          setResponseFlowForThread(threadUuid, (responseFlow) => ({
            ...responseFlow,
            question2: [
              true,
              q.message,
              q.tickers,
              responseRecommendations.isAdvanced,
              responseRecommendations.advancedType,
            ],
          }));
        } else {
          setResponseFlow((responseFlow) => ({
            ...responseFlow,
            question2: [
              true,
              q.message,
              q.tickers,
              responseRecommendations.isAdvanced,
              responseRecommendations.advancedType,
            ],
          }));
        }
      }
      obj.push([true, message_text, tickers]);
    });
    return obj;
  };

  useEffect(() => {
    const pending = thingByThreadUuid || {};
    const responses = testResponseMessageByThreadUuid || {};
    Object.keys(pending).forEach((threadUuid) => {
      if (!pending[threadUuid]) return;
      const responseMessage = responses[threadUuid];
      if (!responseMessage) return;
      updateThreadMessages(threadUuid, (prev) => {
        const last = prev[prev.length - 1];
        if (last && last.sender === "bot") {
          const finalMsg = reconcileFinalBotMessage(last, responseMessage);
          return [...prev.slice(0, -1), finalMsg];
        }
        return [...prev, responseMessage];
      });
      const isActiveThread = activeThreadRef.current?.threadUuid === threadUuid;
      if (!isActiveThread) {
        markThreadUnread(threadUuid);
      }
      setThreadThing(threadUuid, false);
      setThreadTestResponseMessage(threadUuid, "");
    });
  }, [
    thingByThreadUuid,
    testResponseMessageByThreadUuid,
    updateThreadMessages,
    markThreadUnread,
    setThreadThing,
    setThreadTestResponseMessage,
  ]);

  // This code was buggy.. sometimes
  // Sometimes "newActiveThread" is undefined because it's in the prev mapwhere it is setasync
  // const resolveTempId = useCallback(
  //   (tempUuid, realUuid, title) => {
  //     let newActiveThread;
  //     setThreads((prev) =>
  //       prev.map((t) => {
  //         // console.log("What do we have... ?", t.threadUuid, tempUuid)
  //         if (t.threadUuid === tempUuid) {
  //           newActiveThread = { ...t, threadUuid: realUuid, title };
  //           return newActiveThread;
  //         } else {
  //           return t;
  //         }
  //       })
  //     );
  //     // needs to set the activeThread as well.
  //     if (newActiveThread) {
  //       setActiveThread(newActiveThread);
  //     }
  //   },
  //   [setThreads]
  // );

  const rememberResolvedTempThreadMapping = useCallback(
    (tempUuid, realUuid, title, options = {}) => {
      if (!tempUuid || !realUuid || tempUuid === realUuid) return null;

      const { persist = false } = options || {};
      const titleForPersistence = resolvedThreadTitle(
        activeThreadRef.current?.title,
        title,
      );
      resolvedTempThreadMapRef.current = {
        ...(resolvedTempThreadMapRef.current || {}),
        [tempUuid]: realUuid,
      };
      if (persist) {
        rememberActiveChatThread(
          { threadUuid: realUuid, title: titleForPersistence },
          activeChatThreadPersistenceScope,
        );
        updateRequestedChatThreadInUrl(realUuid);
      }

      return titleForPersistence;
    },
    [activeChatThreadPersistenceScope, updateRequestedChatThreadInUrl],
  );

  // This is the correct way to do the function:
  const resolveTempId = useCallback(
    (tempUuid, realUuid, title) => {
      setThreads((prev) =>
        prev.map((t) =>
          t.threadUuid === tempUuid
            ? {
                ...t,
                threadUuid: realUuid,
                title: resolvedThreadTitle(t.title, title),
              }
            : t,
        ),
      );
      setActiveThread((prev) =>
        prev && prev.threadUuid === tempUuid
          ? {
              ...prev,
              threadUuid: realUuid,
              title: resolvedThreadTitle(prev.title, title),
            }
          : prev,
      );
      if (tempUuid && realUuid && tempUuid !== realUuid) {
        const titleForPersistence = rememberResolvedTempThreadMapping(
          tempUuid,
          realUuid,
          title,
          { persist: true },
        );
        if (
          Object.prototype.hasOwnProperty.call(
            loadedMessagesByThreadUuidRef.current || {},
            tempUuid,
          )
        ) {
          loadedMessagesByThreadUuidRef.current = {
            ...(loadedMessagesByThreadUuidRef.current || {}),
            [realUuid]: loadedMessagesByThreadUuidRef.current[tempUuid],
          };
          delete loadedMessagesByThreadUuidRef.current[tempUuid];
        }
        if (activeThreadRef.current?.threadUuid === tempUuid) {
          activeThreadRef.current = {
            ...(activeThreadRef.current || {}),
            threadUuid: realUuid,
            title: titleForPersistence,
          };
          // Flag this as an in-place resolve of the thread we're viewing so
          // ChatBox keeps the scroll position instead of jumping to the top.
          pendingResolvedThreadUuidRef.current = realUuid;
        }
        moveThreadState(tempUuid, realUuid);
        moveQueuedMessagesByThreadUuidRef(tempUuid, realUuid);
        inFlightSendByThreadRef.current = moveThreadDispatch(
          inFlightSendByThreadRef.current,
          tempUuid,
          realUuid,
        );
        moveMessagesByThreadUuid(tempUuid, realUuid);
        moveResponseFlowByThreadUuid(tempUuid, realUuid);
        if (messageThreadsOwnerRef.current === tempUuid) {
          messageThreadsOwnerRef.current = realUuid;
        }
        const shouldBePrimary =
          activeThreadRef.current?.threadUuid === realUuid ||
          activeThreadRef.current?.threadUuid === tempUuid ||
          activeThread?.threadUuid === realUuid ||
          activeThread?.threadUuid === tempUuid;
        moveThreadSubscription(tempUuid, realUuid, {
          setAsPrimary: shouldBePrimary,
        });
      }
    },
    [
      setThreads,
      setActiveThread,
      activeThread?.threadUuid,
      rememberResolvedTempThreadMapping,
      moveThreadState,
      moveQueuedMessagesByThreadUuidRef,
      moveMessagesByThreadUuid,
      moveResponseFlowByThreadUuid,
      moveThreadSubscription,
    ],
  );
  resolveTempIdRef.current = resolveTempId;

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleThreadResolved = (event) => {
      const detail = event?.detail || {};
      if (!detail.tempThreadUuid || !detail.threadUuid) return;
      rememberResolvedTempThreadMapping(
        detail.tempThreadUuid,
        detail.threadUuid,
        detail.threadTitle || "Chat",
      );
    };

    window.addEventListener("incite:thread-resolved", handleThreadResolved);
    return () =>
      window.removeEventListener("incite:thread-resolved", handleThreadResolved);
  }, [rememberResolvedTempThreadMapping]);

  const handleRecommendedAction = useCallback((action) => {
    if (action?.action !== "open_text_messaging_setup") return false;

    setTextSetupContext({
      ...(action || {}),
      source: action.source || "chat_recommended_action",
    });
    setShowTextSetupModal(true);
    return true;
  }, []);

  const chatDispatch = useChatDispatch({
    activeChatThreadPersistenceScope,
    activeThread,
    activeThreadRef,
    adminBaseDirectiveModelOverride: isAdminUser ? adminBaseDirectiveModelOverride : "",
    adminBaseDirectiveReasoningOverride: isAdminUser ? adminBaseDirectiveReasoningOverride : "",
    appProps,
    checkThing,
    clearDataProcessesForThread,
    deviceType,
    enqueueQueuedMessageForThread,
    ensureThreadSubscription,
    inFlightSendByThreadRef,
    isLoggedIn,
    loadingResponse,
    loadingResponseByThreadUuid,
    markQueuedMessageForThread,
    messageThreads,
    messageThreadsOwnerRef,
    messagesByThreadUuidRef,
    navigate,
    preventQuestionInput,
    preventQuestionInputByThreadUuid,
    question,
    queuedMessagesByThreadUuidRef,
    removeQueuedMessageForThread,
    resolveTempId,
    resolvedTempThreadMapRef,
    restoredPendingSecondQuestionRef,
    setActiveThread,
    setButtonOpacity,
    setCancelRequested,
    setConversionGateContext,
    setFreeUnlimitedOnly,
    setHasShownFreeUnlimitedMessage,
    setIdSelected,
    setIsSecondaryExperience,
    setLiveTextOutputting,
    setLoadingResponse,
    setMessageThreads,
    setOpacity,
    setPaywallConversionGateContext,
    setPaywallMessage,
    setPreventQuestionInput,
    setPreventScroll,
    setProBannerSessionQuestionCount,
    setQuestion,
    setRegisterNavMessage,
    setReferralProgram,
    setResponseFlow,
    setResponseFlowForThread,
    setServiceTipPrompt,
    setShowAddHomeModal,
    setShowChatShare,
    setShowLoadingWaitingMessage,
    setShowPastDueModal,
    setShowPaywallModal,
    setShowReferralInviteModal,
    setShowReviewModal,
    setShowServiceTipModal,
    setShowSignUpModal,
    setShowSubOfferModal,
    setShowTextSetupModal,
    setTestResponseMessage,
    setTextSetupContext,
    setThreadLiveTextOutputting,
    setThreadLoadingPhaseDetail,
    setThreadLoadingPhaseMessage,
    setThreadLoadingResponse,
    setThreadPreventQuestionInput,
    setThreadQuestionOnDemand,
    setThreadShowLoadingWaitingMessage,
    setThreadTestResponseMessage,
    setThreadThing,
    setThreads,
    setUserData,
    shiftQueuedMessageForThread,
    updateQueuedMessageForThread,
    updateThreadMessages,
    userData,
  });
  chatDispatchApiRef.current = chatDispatch;
  const {
    editQueuedMessageForThread,
    handleAbortResponse,
    removeQueuedMessageFromThread,
    retryFailedMessageForThread,
  } = chatDispatch;

  // This does the check for free unlimited only on load.
  useEffect(() => {
    if (messageThreads.length > 1) {
      // Check for regular user:
      if (
        userData.user_account_info &&
        userData.user_account_info.free_unlimited_only
      ) {
        setFreeUnlimitedOnly(userData.user_account_info.free_unlimited_only);
      }
      // Check for a pre account user (will not be within user_account_info object since that is not preset for pre account user.)
      else if (userData && userData.free_unlimited_only) {
        setFreeUnlimitedOnly(userData.free_unlimited_only);
      }
    }
  }, [userData.user_account_info, messageThreads && messageThreads.length]);

  useEffect(() => {
    const threadUuid = activeThread?.threadUuid ?? null;
    if (!threadUuid) return;
    if (!loadingResponseByThreadUuid?.[threadUuid]) {
      setThreadLoadingPhaseMessage(threadUuid, null); // reset loading phase for this thread
      setThreadLoadingPhaseDetail(threadUuid, null);
      setThreadLiveTextOutputting(threadUuid, false); // reset live text for this thread
    }
  }, [
    activeThread?.threadUuid,
    loadingResponseByThreadUuid,
    setThreadLoadingPhaseMessage,
    setThreadLoadingPhaseDetail,
    setThreadLiveTextOutputting,
  ]);

  const handleAddThread = () => {
    updateRequestedChatThreadInUrl(null);
    // Invalidate any in-flight selectActiveThread fetch so stale responses are ignored.
    threadSelectionTokenRef.current = threadSelectionTokenRef.current + 1;
    const hasUserEnteredMessage = (threadUuid) => {
      const threadMessages = messagesByThreadUuidRef.current?.[threadUuid];
      if (!Array.isArray(threadMessages) || threadMessages.length === 0) {
        return false;
      }
      return threadMessages.some((message) => {
        if (!message || message.sender !== "human") return false;
        const text =
          typeof message.message === "string" ? message.message.trim() : "";
        const hasAttachments =
          Array.isArray(message.attachments) && message.attachments.length > 0;
        return text.length > 0 || hasAttachments;
      });
    };
    const isEmptyTempThread = (thread) => {
      const threadUuid = thread?.threadUuid;
      if (!isTempChatThreadUuid(threadUuid)) return false;
      return !hasUserEnteredMessage(threadUuid);
    };
    const reusableTempThread = isEmptyTempThread(activeThread)
      ? activeThread
      : Array.isArray(threads)
        ? threads.find((thread) => isEmptyTempThread(thread))
        : null;

    if (reusableTempThread) {
      selectActiveThread(reusableTempThread);
      if (isMobile) {
        setIsSidebarOpen(false);
      }
      return;
    }

    const existingTempCount = Array.isArray(threads)
      ? threads.filter((thread) => isTempChatThreadUuid(thread?.threadUuid))
          .length
      : 0;
    const tempTitle =
      existingTempCount > 0
        ? `New Thread ${existingTempCount + 1}`
        : "New Thread";

    const newThread = {
      threadUuid: `temp-${uuid()}`,
      title: tempTitle,
      isAdminTargeted: false,
    };
    setThreads((prev) => [newThread, ...(Array.isArray(prev) ? prev : [])]);
    setActiveThread(newThread);
    activeThreadRef.current = newThread;
    messageThreadsOwnerRef.current = newThread.threadUuid;
    setMessageThreads([]);
    setLoadingMessages(false);
    ensureResponseFlowForThread(newThread.threadUuid, {
      hideQuestionButtons: !userData?.user_id,
    });

    if (isMobile) {
      // on mobile, automatically close the side panel
      setIsSidebarOpen(false);
    }
  };

  useEffect(() => {
    if (!shouldForceFreshLoadThreadRef.current) return;
    if (hasForcedFreshLoadThreadRef.current) return;
    if (loadingUserData) return;
    if (hasFreshRequestedPersistedChatThread) {
      hasForcedFreshLoadThreadRef.current = true;
      return;
    }
    if (userData?.user_id) {
      hasForcedFreshLoadThreadRef.current = true;
      return;
    }
    if (activeThread || readRememberedActiveChatThread(activeChatThreadPersistenceScope)) {
      hasForcedFreshLoadThreadRef.current = true;
      return;
    }
    handleAddThread();
    hasForcedFreshLoadThreadRef.current = true;
  }, [
    activeChatThreadPersistenceScope,
    activeThread,
    handleAddThread,
    hasFreshRequestedPersistedChatThread,
    loadingUserData,
    userData?.user_id,
  ]);

  const GOOGLE_REVIEW_URL =
    "https://www.google.com/search?q=incite+ai&rlz=1C5ZNUK_enUS1142US1143&oq=Incite+ai&gs_lcrp=EgZjaHJvbWUqCggAEAAY4wIYgAQyCggAEAAY4wIYgAQyEAgBEC4YrwEYxwEYgAQYjgUyBwgCEAAYgAQyBwgDEAAYgAQyBwgEEAAYgAQyBwgFEAAYgAQyBggGEEUYPDIGCAcQRRg80gEHOTIyajBqN6gCALACAA&sourceid=chrome&ie=UTF-8#lrd=0x80c2bf0722b08d53:0x7ccdb9808ae36d74,3,,,,";

  const handleCloseReviewModal = async () => {
    try {
      await updateUserReview(false);
    } catch (e) {
    } finally {
      setShowReviewModal(false);
      setIsGoogleReview(false);
    }
  };

  const handleRating = (rate) => {
    setRating(rate);
  };

  const handleReviewSubmit = async () => {
    try {
      if (isGoogleReview) {
        if (typeof window !== "undefined") {
          window.open(GOOGLE_REVIEW_URL, "_blank", "noopener,noreferrer");
        }
        setShowReviewModal(false);
        setIsGoogleReview(false);
        return;
      }

      if (rating > 0 && rating <= 5) {
        await updateUserReview(true, rating);
      }

      if (rating === 5) {
        setIsGoogleReview(true);
      } else {
        setShowReviewModal(false);
      }
    } catch (err) {
      console.error("Failed to submit review", err);
      setIsGoogleReview(false);
      setShowReviewModal(false);
    }
  };

  const handleServiceTipShown = useCallback(async (promptId) => {
    try {
      await recordServiceTipPromptShown(promptId);
    } catch (e) {
      console.error("Failed to record service tip prompt shown", e);
    }
  }, []);

  const handleServiceTipDismiss = useCallback(async (promptId) => {
    try {
      await dismissServiceTipPrompt(promptId);
    } catch (e) {
      console.error("Failed to dismiss service tip prompt", e);
    }
  }, []);

  const handleServiceTipCheckout = useCallback((promptId, amountMinorUnits) => {
    return createServiceTipCheckoutSession(promptId, amountMinorUnits);
  }, []);

  const handleServiceTipPaymentMethodStatus = useCallback((promptId) => {
    return getServiceTipPaymentMethodStatus(promptId);
  }, []);

  const handleServiceTipSavedPayment = useCallback((promptId, amountMinorUnits) => {
    return payServiceTipWithSavedPaymentMethod(promptId, amountMinorUnits);
  }, []);

  // This ensures that even if we delete a thrad, that we create a new one!
  useEffect(() => {
    if (
      loadingMessageThreads ||
      loadingUserData ||
      Object.keys(userData).length == 0
    )
      return;
    if (activeThread) return;
    if (hasFreshRequestedPersistedChatThread) return;
    if (readRememberedActiveChatThread(activeChatThreadPersistenceScope)) return;
    const isPreAccount = !userData.user_id && !userData.user_account_info;
    if ((threads && threads.length == 0) || (!threads && isPreAccount)) {
      handleAddThread();
    }
  }, [
    activeChatThreadPersistenceScope,
    threads,
    userData,
    loadingUserData,
    loadingMessageThreads,
    hasFreshRequestedPersistedChatThread,
    activeThread,
  ]);

  return (
    <Sidebar
      selectActiveThread={selectActiveThread}
      prefetchThreadMessages={prefetchThreadMessages}
      hasAccount={userData.uuidHasAccount}
      isSidebarOpen={isSidebarOpen}
      setIsSidebarOpen={setIsSidebarOpen}
      handleAddThread={handleAddThread}
      requestedThreadUuid={
        hasFreshRequestedPersistedChatThread ? requestedChatThreadUuid : ""
      }
      onQuestionSubmit={sendUserQuestion}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          backgroundColor: "#FAFAFA",
        }}
      >
        {/* CHAT BOX */}
        <Box sx={{ width: "100%" }}>
          {showTermsModal && (
            <TermsModal
              showTermsModal={showTermsModal}
              setShowTermsModal={setShowTermsModal}
              setHasAcceptedTerms={setHasAcceptedTerms}
            />
          )}
          {showAddHomeModal && (
            <AddAppHomeModal
              showAddHomeModal={showAddHomeModal}
              setShowAddHomeModal={setShowAddHomeModal}
            />
          )}
          <DownloadAppBanner />
          {showSignUpModal && (
            <SignUpModalV2
              userData={userData}
              setUserData={setUserData}
              setShowSignUpModal={setShowSignUpModal}
              showPaywallAfter={showPaywallAfter}
              setShowPaywallModal={setShowPaywallModal}
              isSidebarOpen={isSidebarOpen}
              sidebarOffset={sidebarOffset}
            />
          )}
          {showPaywallModal && (
            <PaywallModal
              appProps={appProps}
              userData={userData}
              setUserData={setUserData}
              setShowPaywallModal={setShowPaywallModal}
              paywallMessage={paywallMessage}
              setShowSignUpModal={setShowSignUpModal}
              setShowPaywallAfter={setShowPaywallAfter}
              conversionGateContext={paywallConversionGateContext}
              isSidebarOpen={isSidebarOpen}
              sidebarOffset={sidebarOffset}
            />
          )}
          {activeInAppNotification && !hasBlockingOverlay && (
            <InAppNotificationModal
              notification={activeInAppNotification}
              onClose={() => setActiveInAppNotification(null)}
              sidebarOffset={sidebarOffset}
            />
          )}
          {showPastDueModal && (
            <PastDueModal
              userData={userData}
              setUserData={setUserData}
              setShowPastDueModal={setShowPastDueModal}
              paywallMessage={paywallMessage}
              setShowSignUpModal={setShowSignUpModal}
              setShowPaywallAfter={setShowPaywallAfter}
              isSidebarOpen={isSidebarOpen}
            />
          )}

          {showSubOfferModal == "offering" && (
            <SubscriptionOfferCoupon
              setShowSubOfferModal={setShowSubOfferModal}
            />
          )}

          <ChatBox
            isDesktop={isDesktop}
            anchorOffsetTop={anchorOffsetTop}
            sidebarOffset={sidebarOffset}
            chatLayout={chatLayout}
            messageThreads={messageThreads}
            isSidebarOpen={isSidebarOpen}
            setQuestion={setQuestion}
            onQuestionSubmit={sendUserQuestion}
            onRecommendedAction={handleRecommendedAction}
            onBeginEditMessage={handleBeginEditMessage}
            onRemoveQueuedMessage={removeQueuedMessageFromThread}
            onRetryQueuedMessage={retryFailedMessageForThread}
            loadingResponse={loadingResponse}
            userData={userData}
            setUserData={setUserData}
            chatRef={chatRef}
            chatContainerRef={chatContainerRef}
            chatMessagesContainerRef={chatMessagesContainerRef}
            pendingResolvedThreadUuidRef={pendingResolvedThreadUuidRef}
            hasShownHistory={hasShownHistory}
            regenButtonRecommendations={regenButtonRecommendations}
            preventQuestionInput={preventQuestionInput || Boolean(editingMessage)}
            setPreventQuestionInput={setPreventQuestionInput}
            showLoadingWaitingMessage={showLoadingWaitingMessage}
            setShowLoadingWaitingMessage={setShowLoadingWaitingMessage}
            useFullWidth={useFullWidth}
            isSecondaryExperience={isSecondaryExperience}
            isNewUUID={isNewUUID}
            loadingUserData={loadingUserData}
            setIsSecondaryExperience={setIsSecondaryExperience}
            showSignInButtons={showSignInButtons}
            setQuestionOnDemand={setQuestionOnDemand}
            editingMessage={editingMessage}
            onSubmitEditMessage={submitEditedMessage}
            onCancelEditMessage={handleCancelEdit}
            skipHideThreadUuid={skipHideThreadUuid}
          />
        </Box>

        <ReviewModal
          open={showReviewModal}
          onClose={handleCloseReviewModal}
          onSubmit={handleReviewSubmit}
          onRating={handleRating}
          rating={rating}
          isGoogle={isGoogleReview}
        />
        <ReferralInviteModal
          open={showReferralInviteModal}
          onClose={() => setShowReferralInviteModal(false)}
          program={referralProgram}
          onProgramUpdate={setReferralProgram}
          surface="chat"
          dismissOnClose={true}
        />
        <ServiceTipPromptModal
          open={showServiceTipModal}
          prompt={serviceTipPrompt}
          onClose={() => setShowServiceTipModal(false)}
          onShown={handleServiceTipShown}
          onDismiss={handleServiceTipDismiss}
          onCheckout={handleServiceTipCheckout}
          onPaymentMethodStatus={handleServiceTipPaymentMethodStatus}
          onSavedPaymentTip={handleServiceTipSavedPayment}
        />
        <TextMessagingSetupModal
          open={showTextSetupModal}
          source={textSetupContext?.source || "chat_text_setup"}
          context={textSetupContext || { source: "chat_text_setup", surface: "chat" }}
          onClose={() => setShowTextSetupModal(false)}
        />
        {/* NAVBAR */}
        <ProBanner
          userData={userData}
          isLoggedIn={isLoggedIn}
          loadingUserData={loadingUserData}
          isSidebarOpen={isSidebarOpen}
          isDataProcessesOpen={isDataProcessesOpen}
          chatLayout={chatLayout}
          sessionQuestionCount={proBannerSessionQuestionCount}
        />
        <Box
          sx={{ position: "fixed", left: "0px", top: "0px", width: "100%" }}
          ref={navRef}
        >
          {!loadingUserData && (
            <Navbar
              isSidebarOpen={isSidebarOpen}
              isDataProcessesOpen={isDataProcessesOpen}
              chatLayout={chatLayout}
              setIsSidebarOpen={setIsSidebarOpen}
              appProps={appProps}
              allowAbout={true}
              showSubscriptionStatus={true}
              onQuestionSubmit={sendUserQuestion}
            />
          )}
        </Box>
        {isAdminUser && (
          <AdminChatModelSwitcher
            catalog={adminChatModelCatalog}
            selectedModelName={adminBaseDirectiveModelOverride}
            onSelectedModelNameChange={handleAdminBaseDirectiveModelChange}
            selectedReasoningEffort={adminBaseDirectiveReasoningOverride}
            onSelectedReasoningEffortChange={handleAdminBaseDirectiveReasoningChange}
            loading={adminChatModelCatalogLoading}
            error={adminChatModelCatalogError}
            chatLayout={chatLayout}
          />
        )}
        <Box sx={{ width: "100%", marginBottom: "10px" }}>
          <Input
            userData={userData}
            sidebarOffset={sidebarOffset}
            chatLayout={chatLayout}
            isDesktop={isDesktop}
            showSignInButtons={showSignInButtons}
            onFocus={onFocus}
            onBlur={onBlur}
            question={question}
            setQuestion={setQuestion}
            onQuestionSubmit={handleInputSubmit}
            placeholderText={placeholderText}
            preventQuestionInput={preventQuestionInput}
            inputPlaceholderText={inputPlaceholderText}
            activeThread={activeThread}
            queuedMessages={queuedMessages}
            onEditQueuedMessage={editQueuedMessageForThread}
            onRemoveQueuedMessage={removeQueuedMessageFromThread}
            cancelRequested={cancelRequested}
            handleAbortResponse={handleAbortResponse}
            loadingResponse={loadingResponse}
            loadingMessages={loadingMessages}
            loadingUserData={loadingUserData}
          />
          {/* <PrechatVersion/> */}
        </Box>
      </Box>
      <DataOutput loadingResponse={loadingResponse} />
    </Sidebar>
  );
};

export default ChatPage;
